/*
 * Seven Oceans storefront behaviour tracker (theme script, asset: so-tracker.js)
 *
 * Sends light, anonymous behaviour events to POST <dashboard>/api/collect/session
 * (contract: docs/behaviour-plan.md). Dependency free, ES2018.
 *
 * COLLECTS (only after analytics consent): sections seen + dwell, scroll milestones,
 *   click positions inside sections (element descriptor like "button#add-to-cart"),
 *   rage/dead clicks, quick-backs, JS error CLASS names, web vitals, add-to-cart,
 *   campaign source (utm_*, which click id was present, never its value), device class.
 * NEVER COLLECTS: text, input values, DOM, query strings, IP, user agent, emails.
 * SESSION ID: random, sessionStorage only ("so_sid"), no cookies of our own.
 * EXITS ON: /account, /checkouts, /checkout, /orders, /cart/c/ and the theme editor.
 * CLARITY: loaded only after consent, and only if a project id is configured.
 */
(function () {
  'use strict';

  // ---- Config (override with window.SO_TRACKER_CONFIG before this script, for local tests) ----
  var ENDPOINT = 'https://REPLACE-WITH-DASHBOARD-URL/api/collect/session';
  var SHOP = 'sevenoceans.dk';
  var CLARITY_PROJECT_ID = 'yw8y7eec80'; // Seven Oceans project; empty = Microsoft Clarity off

  var W = window;
  var D = document;
  if (W.__soTracker) return;

  var CFG = { endpoint: ENDPOINT, shop: SHOP, clarityProjectId: CLARITY_PROJECT_ID, debug: false };
  var override = W.SO_TRACKER_CONFIG;
  if (override && typeof override === 'object') {
    for (var ck in override) if (Object.prototype.hasOwnProperty.call(override, ck)) CFG[ck] = override[ck];
    if (override.clarity && !override.clarityProjectId) CFG.clarityProjectId = override.clarity;
  }

  // ---- Never run on private pages or in the theme editor ----
  if (W.Shopify && W.Shopify.designMode) return;
  if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:account|checkouts?|orders|cart\/c)(?:\/|$)/i.test(location.pathname)) return;
  W.__soTracker = true;

  var PATH = location.pathname.slice(0, 200);
  var SESSION_MS = 30 * 60 * 1000;
  var FLUSH_MS = 10000;
  var MAX_BATCH = 50;
  var MAX_BUFFER = 200;
  var MAX_BODY = 31000;
  var MILESTONES = [25, 50, 75, 90, 100];
  var INTERACTIVE = 'a,button,input,select,textarea,label,summary,[role="button"],[role="link"]';
  var SECTION_SEL = '[id^="shopify-section-"]';
  var T0 = Date.now(); // when this page started

  function log() {
    if (CFG.debug && W.console) W.console.log.apply(W.console, ['[so-tracker]'].concat([].slice.call(arguments)));
  }

  // ---- sessionStorage with an in-memory fallback (privacy modes can throw) ----
  var mem = {};
  function sget(k) {
    try {
      return W.sessionStorage.getItem(k);
    } catch (e) {
      return mem[k] === undefined ? null : mem[k];
    }
  }
  function sset(k, v) {
    try {
      W.sessionStorage.setItem(k, v);
    } catch (e) {
      mem[k] = v;
    }
  }

  // ---- State ----
  var running = false;
  var buf = [];
  var sid = null;
  var off = []; // cleanup functions
  var observers = [];
  var timers = [];
  var secs = [];
  var secMap = typeof Map === 'function' ? new Map() : null;
  var live = typeof Set === 'function' ? new Set() : null;
  var pending = []; // clicks waiting for the dead-click decision
  var pageEvt = null;
  var lastTick = Date.now();
  var lastMut = 0;
  var lastScroll = 0;
  var lastFocus = 0;
  var lastCartAdd = 0;
  var patched = false;
  var origFetch = null;
  var clarityLoaded = false;
  var vitals = { LCP: null, CLS: 0, INP: null, sent: false };
  var errCount = 0;
  var milestonesDone = {};
  var recent = []; // recent clicks for rage detection
  var burstFlagged = false;

  // ---- Helpers ----
  function newSid() {
    var chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    var out = 's_';
    var arr = new Uint8Array(22);
    if (W.crypto && W.crypto.getRandomValues) W.crypto.getRandomValues(arr);
    else for (var i = 0; i < 22; i++) arr[i] = Math.floor(Math.random() * 256);
    for (var j = 0; j < 22; j++) out += chars.charAt(arr[j] % 36);
    return out;
  }

  function trimParam(params, k) {
    var v = params.get(k);
    v = v ? v.trim().slice(0, 100) : '';
    return v || null;
  }

  function buildMeta() {
    var params = new URLSearchParams(location.search);
    var clickId = null;
    ['fbclid', 'gclid', 'ttclid', 'msclkid'].forEach(function (k) {
      if (!clickId && params.has(k)) clickId = k; // which id, never its value
    });
    var ref = null;
    try {
      if (D.referrer) {
        var h = new URL(D.referrer).hostname;
        ref = h && h !== location.hostname ? h.slice(0, 100) : null;
      }
    } catch (e) {
      ref = null;
    }
    var w = W.innerWidth || 0;
    return {
      landing_path: PATH,
      referrer_host: ref,
      utm_source: trimParam(params, 'utm_source'),
      utm_medium: trimParam(params, 'utm_medium'),
      utm_campaign: trimParam(params, 'utm_campaign'),
      utm_content: trimParam(params, 'utm_content'),
      utm_term: trimParam(params, 'utm_term'),
      click_id: clickId,
      device: w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop',
      viewport_w: w,
      viewport_h: W.innerHeight || 0,
      lang: (D.documentElement.lang || '').slice(0, 10) || null,
    };
  }

  function getMeta() {
    try {
      return JSON.parse(sget('so_meta')) || {};
    } catch (e) {
      return {};
    }
  }

  /** Current session id; starts a new session after 30 min of inactivity. */
  function ensureSession() {
    var now = Date.now();
    var cur = sget('so_sid');
    var last = +sget('so_last') || 0;
    if (!cur || !/^s_[a-z0-9]{22}$/.test(cur) || now - last > SESSION_MS) {
      var rotated = !!sid;
      cur = newSid();
      sset('so_sid', cur);
      sset('so_meta', JSON.stringify(buildMeta()));
      sset('so_meta_sent', '');
      sset('so_cartattr', '');
      sid = cur;
      if (rotated) clarityTags();
    }
    sid = cur;
    sset('so_last', String(now));
    return cur;
  }

  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    off.push(function () {
      target.removeEventListener(type, fn, opts);
    });
  }

  function emit(e) {
    if (!running) return null;
    ensureSession();
    e.ts = Date.now();
    if (!e.path) e.path = PATH;
    if (buf.length >= MAX_BUFFER) buf.shift();
    buf.push(e);
    return e;
  }

  // ---- Sending ----
  function send(body) {
    log('send', body.length + ' bytes');
    var ok = false;
    try {
      if (navigator.sendBeacon) ok = navigator.sendBeacon(CFG.endpoint, new Blob([body], { type: 'text/plain' }));
    } catch (e) {
      ok = false;
    }
    if (!ok) {
      try {
        (origFetch || W.fetch).call(W, CFG.endpoint, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: body,
        }).catch(function () {});
      } catch (e) {
        // give up silently
      }
    }
  }

  function flush() {
    if (!running || buf.length === 0) return;
    // No dashboard address yet (before deploy): drop events, send nothing. Clarity still runs.
    if (!CFG.endpoint || CFG.endpoint.indexOf('REPLACE-WITH') !== -1) {
      buf = [];
      return;
    }
    if (pageEvt) pageEvt.doc_h = docHeight();
    ensureSession();
    var meta = sget('so_meta_sent') === sid ? null : getMeta();
    while (buf.length > 0) {
      var n = Math.min(MAX_BATCH, buf.length);
      var body;
      for (;;) {
        var payload = { shop: CFG.shop, v: 1, sid: sid, consent: { analytics: true } };
        if (meta) payload.session = meta;
        payload.events = buf.slice(0, n);
        body = JSON.stringify(payload);
        if (body.length <= MAX_BODY || n <= 1) break;
        n = Math.ceil(n / 2);
      }
      buf.splice(0, n);
      send(body);
      if (meta) {
        sset('so_meta_sent', sid);
        meta = null;
      }
    }
  }

  // ---- Page info ----
  function docHeight() {
    var de = D.documentElement;
    return Math.round(Math.max(de.scrollHeight, D.body ? D.body.scrollHeight : 0));
  }

  function pageType() {
    try {
      var m = W.ShopifyAnalytics && W.ShopifyAnalytics.meta && W.ShopifyAnalytics.meta.page;
      if (m && m.pageType) return String(m.pageType).slice(0, 30);
    } catch (e) {
      // fall through
    }
    var meta = D.querySelector('meta[name="page-type"],meta[name="template"]');
    if (meta && meta.content) return meta.content.slice(0, 30);
    var cls = (D.body && D.body.className) || '';
    var m2 = /(?:^|\s)template-([a-z]+)/.exec(cls);
    if (m2) return m2[1];
    if (PATH === '/' || /^\/[a-z]{2}\/?$/.test(PATH)) return 'index';
    if (/\/products\//.test(PATH)) return 'product';
    if (/\/collections\//.test(PATH)) return 'collection';
    if (/\/cart/.test(PATH)) return 'cart';
    if (/\/blogs\/[^/]+\/[^/]+/.test(PATH)) return 'article';
    if (/\/blogs\//.test(PATH)) return 'blog';
    if (/\/search/.test(PATH)) return 'search';
    if (/\/pages\//.test(PATH)) return 'page';
    return 'other';
  }

  function productHandle() {
    var m = /\/products\/([^/]+)/.exec(PATH);
    return m ? m[1].slice(0, 100) : null;
  }

  // ---- Microsoft Clarity (after consent only) ----
  function cl() {
    if (W.clarity) W.clarity.apply(W, arguments);
  }

  function clarityTags() {
    if (!clarityLoaded) return;
    var meta = getMeta();
    cl('set', 'so_sid', sid);
    ['utm_source', 'utm_campaign', 'utm_content'].forEach(function (k) {
      if (meta[k]) cl('set', k, meta[k]);
    });
    cl('set', 'page_type', pageType());
    var h = productHandle();
    if (h) cl('set', 'product', h);
  }

  function loadClarity() {
    var id = CFG.clarityProjectId;
    if (!id) return;
    if (!clarityLoaded) {
      (function (c, l, a, r, i) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        var t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        var y = l.getElementsByTagName(r)[0];
        if (y && y.parentNode) y.parentNode.insertBefore(t, y);
        else l.head.appendChild(t);
      })(W, D, 'clarity', 'script', id);
      clarityLoaded = true;
    }
    cl('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
    clarityTags();
  }

  // ---- Sections: dwell and how much of each was seen ----
  function sectionId(el) {
    return el.id.replace(/^shopify-section-/, '').replace(/^(?:template|sections)--\d+__/, '').slice(0, 80);
  }

  function isMainContent(el) {
    if (/header|footer|announcement/i.test(el.id)) return false;
    if (/shopify-section-group-(?:header|footer)|section-header|section-footer/i.test(el.className || '')) return false;
    return !el.closest('header,footer');
  }

  function initSections() {
    var nodes = D.querySelectorAll(SECTION_SEL);
    var index = 0;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!isMainContent(el)) continue;
      var id = sectionId(el);
      var cm = /(?:^|\s)section-([\w-]+)/.exec(el.className || '');
      var s = {
        el: el,
        id: id,
        type: (cm && cm[1]) || el.getAttribute('data-section-type') || id,
        index: index++,
        dwell: 0,
        from: Infinity,
        to: 0,
        pct: 0,
        repDwell: -1,
        repPct: -1,
      };
      secs.push(s);
      if (secMap) secMap.set(el, s);
    }
    if (!secs.length) return;
    if (typeof IntersectionObserver === 'function' && secMap && live) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var s2 = secMap.get(en.target);
          if (!s2) return;
          if (en.isIntersecting) live.add(s2);
          else live.delete(s2);
        });
      });
      secs.forEach(function (s3) {
        io.observe(s3.el);
      });
      observers.push(io);
    } else if (live) {
      secs.forEach(function (s4) {
        live.add(s4);
      });
    }
    timers.push(setInterval(tick, 250));
  }

  function measure(s, dt) {
    var r = s.el.getBoundingClientRect();
    var vh = W.innerHeight || 1;
    if (r.height < 1) return;
    var from = Math.max(0, -r.top);
    var to = Math.min(r.height, vh - r.top);
    if (to <= from) return;
    s.from = Math.min(s.from, from);
    s.to = Math.max(s.to, to);
    s.pct = Math.min(100, Math.round(((s.to - s.from) / r.height) * 100));
    var seen = to - from;
    // counts as "read" when 30% of the section or 40% of the viewport is covered
    if (seen / r.height >= 0.3 || seen / vh >= 0.4) s.dwell += dt;
  }

  function tick() {
    var now = Date.now();
    var dt = Math.min(now - lastTick, 600);
    lastTick = now;
    if (!running || D.visibilityState !== 'visible' || !live) return;
    sset('so_last', String(now));
    live.forEach(function (s) {
      measure(s, dt);
    });
  }

  function emitSections() {
    if (live)
      live.forEach(function (s) {
        measure(s, 0);
      });
    secs.forEach(function (s) {
      if (s.pct <= 0 || (s.dwell === s.repDwell && s.pct === s.repPct)) return;
      s.repDwell = s.dwell;
      s.repPct = s.pct;
      // Values are cumulative for the page: the latest event per section wins.
      emit({
        t: 'section',
        section_id: s.id,
        section_type: String(s.type).slice(0, 60),
        index: s.index,
        dwell_ms: Math.round(s.dwell),
        seen_pct: s.pct,
      });
    });
  }

  // ---- Scroll milestones ----
  var scrollQueued = false;
  function onScroll() {
    lastScroll = Date.now();
    if (scrollQueued) return;
    scrollQueued = true;
    W.requestAnimationFrame(function () {
      scrollQueued = false;
      checkScroll();
    });
  }

  function checkScroll() {
    if (!running) return;
    var h = docHeight() || 1;
    var depth = ((W.pageYOffset + (W.innerHeight || 0) + 2) / h) * 100;
    MILESTONES.forEach(function (m) {
      if (!milestonesDone[m] && depth >= m) {
        milestonesDone[m] = true;
        emit({ t: 'scroll', depth_pct: m });
      }
    });
  }

  // ---- Clicks ----
  function describe(t) {
    var tag = t.tagName.toLowerCase();
    var d = tag;
    var id = t.getAttribute('id');
    if (id) d += '#' + id.replace(/[^\w-]/g, '').slice(0, 40);
    if (tag === 'a') {
      var h = t.getAttribute('href');
      if (h) {
        try {
          var u = new URL(h, location.href);
          if (u.host === location.host) d += '[href=' + u.pathname.slice(0, 60) + ']';
        } catch (e) {
          // ignore unparsable href
        }
      }
    }
    return d.slice(0, 120);
  }

  function looksClickable(t, tag) {
    if (/^(?:img|picture|svg|video|figure)$/.test(tag)) return true;
    try {
      return W.getComputedStyle(t).cursor === 'pointer';
    } catch (e) {
      return false;
    }
  }

  function onClick(e) {
    if (!running) return;
    var t = e.target;
    if (t && t.nodeType !== 1) t = t.parentElement;
    if (!t || !t.closest) return;
    var now = Date.now();
    var ia = t.closest(INTERACTIVE);
    var sec = t.closest(SECTION_SEL);
    var cx = e.clientX;
    var cy = e.clientY;
    if (!cx && !cy && e.detail === 0) {
      // keyboard-triggered click: use the element centre
      var tr = (ia || t).getBoundingClientRect();
      cx = tr.left + tr.width / 2;
      cy = tr.top + tr.height / 2;
    }
    var px = cx + W.pageXOffset;
    var py = cy + W.pageYOffset;
    var xPct;
    var yPx;
    if (sec) {
      var r = sec.getBoundingClientRect();
      xPct = r.width > 0 ? ((cx - r.left) / r.width) * 100 : 0;
      yPx = cy - r.top;
    } else {
      xPct = ((W.innerWidth || 1) > 0 ? cx / (W.innerWidth || 1) : 0) * 100;
      yPx = py;
    }
    xPct = Math.round(Math.max(0, Math.min(100, xPct)) * 10) / 10;

    // Rage: 3+ clicks within 700 ms inside a 30 px radius. Flag the 3rd click, once per burst.
    recent = recent.filter(function (c) {
      return now - c.t <= 700;
    });
    if (recent.length === 0) burstFlagged = false;
    recent.push({ t: now, x: px, y: py });
    var near = recent.filter(function (c) {
      return Math.abs(c.x - px) <= 30 && Math.abs(c.y - py) <= 30;
    }).length;
    var flag = null;
    if (near >= 3 && !burstFlagged) {
      burstFlagged = true;
      flag = 'rage';
      cl('upgrade', 'rage');
    }

    var ev = {
      t: 'click',
      section_id: sec ? sectionId(sec) : null,
      el: describe(ia || t),
      x_pct: xPct,
      y_px: Math.max(0, Math.round(yPx)),
      page_y: Math.max(0, Math.round(py)),
      flag: flag,
    };

    var tag = t.tagName.toLowerCase();
    if (!flag && !ia && looksClickable(t, tag)) {
      // Possible dead click: wait up to 1 s for any reaction before deciding.
      var p = { ev: ev, at: now, timer: null };
      p.timer = setTimeout(function () {
        settle(p, true);
      }, 1000);
      pending.push(p);
    } else {
      emit(ev);
    }
  }

  function settle(p, check) {
    var i = pending.indexOf(p);
    if (i === -1) return;
    pending.splice(i, 1);
    clearTimeout(p.timer);
    if (check && lastMut < p.at && lastScroll < p.at && lastFocus < p.at) p.ev.flag = 'dead';
    emit(p.ev);
  }

  function settleAll() {
    pending.slice().forEach(function (p) {
      settle(p, false); // leaving the page counts as a reaction
    });
  }

  // ---- Quick-back: left a page within 5 s and came straight back ----
  function checkQuickback() {
    try {
      var prev = JSON.parse(sget('so_prev') || 'null');
      if (prev && prev.i && prev.p !== PATH && prev.e && prev.e - prev.a < 5000) {
        var ev = emit({ t: 'quickback', ms: Math.max(0, prev.e - prev.a) });
        if (ev) ev.path = prev.p;
      }
    } catch (e) {
      // ignore bad storage
    }
  }

  function markArrival() {
    var internal = false;
    try {
      internal = !!D.referrer && new URL(D.referrer).host === location.host;
    } catch (e) {
      internal = false;
    }
    sset('so_prev', JSON.stringify({ p: PATH, a: T0, e: 0, i: internal }));
  }

  function markLeave() {
    try {
      var prev = JSON.parse(sget('so_prev') || 'null');
      if (prev && prev.p === PATH) {
        prev.e = Date.now();
        sset('so_prev', JSON.stringify(prev));
      }
    } catch (e) {
      // ignore bad storage
    }
  }

  function navType() {
    try {
      var n = W.performance.getEntriesByType('navigation')[0];
      return n ? n.type : '';
    } catch (e) {
      return '';
    }
  }

  // ---- Errors (class name only, never the message) ----
  function cleanName(n) {
    return String(n || 'Error').replace(/[^\w$.]/g, '').slice(0, 40) || 'Error';
  }
  function onError(e) {
    if (errCount >= 5) return;
    if (e && e.target && e.target !== W) return; // resource load errors
    errCount++;
    emit({ t: 'error', name: cleanName(e && e.error ? e.error.name : 'ScriptError') });
  }
  function onRejection(e) {
    if (errCount >= 5) return;
    errCount++;
    var r = e && e.reason;
    emit({ t: 'error', name: cleanName(r && r.name ? r.name : 'UnhandledRejection') });
  }

  // ---- Web vitals ----
  function observe(type, fn, extra) {
    try {
      var po = new PerformanceObserver(function (list) {
        list.getEntries().forEach(fn);
      });
      var opts = { type: type, buffered: true };
      if (extra) for (var k in extra) opts[k] = extra[k];
      po.observe(opts);
      observers.push(po);
    } catch (e) {
      // entry type not supported
    }
  }

  function initVitals() {
    if (typeof PerformanceObserver !== 'function') return;
    observe('largest-contentful-paint', function (en) {
      vitals.LCP = en.startTime;
    });
    observe('layout-shift', function (en) {
      if (!en.hadRecentInput) vitals.CLS += en.value;
    });
    observe(
      'event',
      function (en) {
        if (en.interactionId > 0 && (vitals.INP === null || en.duration > vitals.INP)) vitals.INP = en.duration;
      },
      { durationThreshold: 40 }
    );
  }

  function sendVitals() {
    if (vitals.sent) return;
    vitals.sent = true;
    if (vitals.LCP !== null) emit({ t: 'vital', name: 'LCP', value: Math.round(vitals.LCP) });
    emit({ t: 'vital', name: 'CLS', value: Math.round(vitals.CLS * 1000) / 1000 });
    if (vitals.INP !== null) emit({ t: 'vital', name: 'INP', value: Math.round(vitals.INP) });
  }

  // ---- Add to cart ----
  var CART_ADD = /\/cart\/add(?:\.js)?(?:[?#]|$)/;

  function onCartAdd() {
    if (!running) return;
    var now = Date.now();
    if (now - lastCartAdd < 1500) return; // form submit + fetch for the same click
    lastCartAdd = now;
    emit({ t: 'cart_add' });
    cl('event', 'cart_add');
    cl('upgrade', 'cart_add');
    setCartSid();
  }

  /** Writes the session id to the cart so the order carries it (read by the custom pixel). */
  function setCartSid() {
    if (sget('so_cartattr') === sid) return;
    sset('so_cartattr', sid);
    try {
      (origFetch || W.fetch)
        .call(W, '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _so_sid: sid } }),
        })
        .catch(function () {});
    } catch (e) {
      // ignore
    }
  }

  function urlOf(input) {
    return typeof input === 'string' ? input : (input && input.url) || String(input);
  }

  function patchNetwork() {
    if (patched) return;
    patched = true;
    origFetch = W.fetch;
    if (typeof origFetch === 'function') {
      W.fetch = function (input) {
        var p = origFetch.apply(this, arguments);
        try {
          if (running && CART_ADD.test(urlOf(input))) {
            p.then(
              function (res) {
                if (res && res.ok) onCartAdd();
              },
              function () {}
            );
          }
        } catch (e) {
          // never break the shop
        }
        return p;
      };
    }
    var proto = W.XMLHttpRequest && W.XMLHttpRequest.prototype;
    if (proto) {
      var oOpen = proto.open;
      var oSend = proto.send;
      proto.open = function (method, url) {
        try {
          this.__soCart = CART_ADD.test(String(url));
        } catch (e) {
          this.__soCart = false;
        }
        return oOpen.apply(this, arguments);
      };
      proto.send = function () {
        if (this.__soCart) {
          this.addEventListener('loadend', function () {
            if (this.status >= 200 && this.status < 300) onCartAdd();
          });
        }
        return oSend.apply(this, arguments);
      };
    }
  }

  function onSubmit(e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM') return;
    var action = f.getAttribute('action') || '';
    if (CART_ADD.test(action)) onCartAdd();
  }

  // ---- Hide / lifecycle ----
  function onHide() {
    if (!running) return;
    settleAll();
    emitSections();
    sendVitals();
    flush();
  }

  function onVisibility() {
    if (D.visibilityState === 'hidden') onHide();
    else lastTick = Date.now();
  }

  function onPageHide() {
    onHide();
    markLeave();
  }

  function onPageShow(e) {
    if (!e.persisted || !running) return; // restored from the back/forward cache
    T0 = Date.now();
    ensureSession();
    checkQuickback();
    markArrival();
  }

  // ---- Start / stop (consent) ----
  function start() {
    if (running) return;
    running = true;
    lastTick = Date.now();
    ensureSession();
    log('start', sid);
    patchNetwork();
    loadClarity();

    if (D.readyState === 'loading') on(D, 'DOMContentLoaded', initSections);
    else initSections();

    pageEvt = emit({ t: 'page', page_type: pageType(), handle: productHandle(), doc_h: docHeight() });
    if (navType() === 'back_forward') checkQuickback();
    markArrival();
    initVitals();

    if (typeof MutationObserver === 'function' && D.body) {
      var mo = new MutationObserver(function () {
        lastMut = Date.now();
      });
      mo.observe(D.body, { childList: true, subtree: true, attributes: true, characterData: true });
      observers.push(mo);
    }

    on(D, 'click', onClick, true);
    on(D, 'submit', onSubmit, true);
    on(D, 'focusin', function () {
      lastFocus = Date.now();
    });
    on(W, 'scroll', onScroll, { passive: true });
    on(W, 'error', onError);
    on(W, 'unhandledrejection', onRejection);
    on(D, 'visibilitychange', onVisibility);
    on(W, 'pagehide', onPageHide);
    on(W, 'pageshow', onPageShow);
    timers.push(setInterval(flush, FLUSH_MS));
    checkScroll();
  }

  function stop() {
    if (!running) return;
    log('stop');
    running = false;
    buf = [];
    secs = [];
    if (live) live.clear();
    pending.forEach(function (p) {
      clearTimeout(p.timer);
    });
    pending = [];
    off.forEach(function (fn) {
      fn();
    });
    off = [];
    observers.forEach(function (o) {
      try {
        o.disconnect();
      } catch (e) {
        // ignore
      }
    });
    observers = [];
    timers.forEach(clearInterval);
    timers = [];
    if (clarityLoaded) cl('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
  }

  // ---- Consent wiring ----
  function allowed() {
    try {
      var cp = W.Shopify && W.Shopify.customerPrivacy;
      return !!(cp && cp.analyticsProcessingAllowed() === true);
    } catch (e) {
      return false;
    }
  }

  D.addEventListener('visitorConsentCollected', function (e) {
    var d = e && e.detail;
    if (d && typeof d.analyticsAllowed === 'boolean') {
      if (d.analyticsAllowed) start();
      else stop();
    } else if (allowed()) start();
    else stop();
  });

  var S = W.Shopify;
  if (S && S.customerPrivacy) {
    if (allowed()) start();
  } else if (S && typeof S.loadFeatures === 'function') {
    S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      if (!err && allowed()) start();
    });
  }
})();
